import * as Primitive from '@radix-ui/react-toast'
import { XIcon } from 'lucide-react'
import type { ComponentProps } from 'react'
import { cn } from '../lib/cn'
import { buttonStyles } from './button'

export type ToastTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

const toneStyles: Record<ToastTone, string> = {
  neutral: 'border-l-foreground/45',
  info: 'border-l-info-foreground',
  success: 'border-l-success-foreground',
  warning: 'border-l-warning-foreground',
  danger: 'border-l-danger-foreground',
}

export function ToastProvider(props: ComponentProps<typeof Primitive.Provider>) {
  return <Primitive.Provider {...props} />
}

export function ToastViewport({
  className,
  label,
  ...props
}: ComponentProps<typeof Primitive.Viewport> & { label: string }) {
  return (
    <Primitive.Viewport
      {...props}
      label={label}
      data-slot="toast-viewport"
      className={cn(
        'fixed right-0 bottom-0 z-[100] flex max-h-dvh w-full list-none flex-col gap-3 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] outline-none sm:w-[25rem] sm:max-w-[calc(100vw-2rem)] sm:p-6 sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]',
        className,
      )}
    />
  )
}

export function Toast({
  className,
  tone = 'neutral',
  ...props
}: ComponentProps<typeof Primitive.Root> & { tone?: ToastTone }) {
  return (
    <Primitive.Root
      {...props}
      data-slot="toast"
      data-tone={tone}
      className={cn(
        'relative flex w-full items-start gap-3 overflow-hidden rounded-control border border-l-4 border-border bg-card p-4 pr-12 text-card-foreground shadow-overlay',
        'motion-reduce:data-[state=open]:animate-none motion-reduce:data-[swipe=end]:animate-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-right-full',
        'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform data-[swipe=cancel]:duration-200 data-[swipe=end]:animate-out data-[swipe=end]:slide-out-to-right-full',
        toneStyles[tone],
        className,
      )}
    />
  )
}

export function ToastIcon({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      data-slot="toast-icon"
      className={cn('mt-0.5 shrink-0 [&>svg]:size-5', className)}
    />
  )
}

export function ToastContent({ className, ...props }: ComponentProps<'div'>) {
  return <div {...props} data-slot="toast-content" className={cn('min-w-0 flex-1', className)} />
}

export function ToastTitle({ className, ...props }: ComponentProps<typeof Primitive.Title>) {
  return (
    <Primitive.Title
      {...props}
      data-slot="toast-title"
      className={cn('text-sm font-semibold leading-5', className)}
    />
  )
}

export function ToastDescription({
  className,
  ...props
}: ComponentProps<typeof Primitive.Description>) {
  return (
    <Primitive.Description
      {...props}
      data-slot="toast-description"
      className={cn('mt-1 text-sm leading-5 text-muted-foreground', className)}
    />
  )
}

export function ToastAction({ className, ...props }: ComponentProps<typeof Primitive.Action>) {
  return (
    <Primitive.Action
      {...props}
      type="button"
      data-slot="toast-action"
      className={buttonStyles({
        appearance: 'ghost',
        tone: 'accent',
        size: 'sm',
        className: cn('-my-2 shrink-0 self-center', className),
      })}
    />
  )
}

export function ToastClose({
  className,
  label,
  ...props
}: Omit<ComponentProps<typeof Primitive.Close>, 'aria-label' | 'children'> & { label: string }) {
  return (
    <Primitive.Close
      {...props}
      type="button"
      aria-label={label}
      data-slot="toast-close"
      className={cn(
        'absolute top-1.5 right-1.5 inline-flex size-10 items-center justify-center rounded-full text-muted-foreground transition-colors touch-manipulation hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        className,
      )}
    >
      <XIcon aria-hidden="true" className="size-4" />
    </Primitive.Close>
  )
}
